import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import avatar1 from "../../assets/images/users/avatar-1.jpg";
import { getStoredSuperAdminAuth } from "../../helpers/super_admin_auth_storage";
import {
  getSupportPollBaseMs,
  isSupportBeepMuted,
  setSupportBeepMuted,
  setSupportPollBaseMs,
  SUPPORT_POLL_BASE_OPTIONS_MS,
  type SupportPollBaseMs,
} from "../../helpers/supportTicketUx";
import {
  superAdminLogout,
  superAdminUpdatePassword,
} from "../../services/authService";
import { logoutUserSuccess } from "../../slices/auth/login/reducer";

const resolveSuperAdminDisplayName = (): string => {
  const session = getStoredSuperAdminAuth();
  const user = session?.user;

  if (!user) {
    return "Super Admin";
  }

  if (typeof user.email === "string" && user.email.trim() !== "") {
    return user.email;
  }

  if (typeof user.name === "string" && user.name.trim() !== "") {
    return user.name;
  }

  return "Super Admin";
};

const SuperAdminProfileDropdown = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isProfileDropdown, setIsProfileDropdown] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [beepMuted, setBeepMuted] = useState(() => isSupportBeepMuted());
  const [pollBaseMs, setPollBaseMsState] = useState<SupportPollBaseMs | null>(
    () => getSupportPollBaseMs()
  );

  const userName = useMemo(() => resolveSuperAdminDisplayName(), []);

  const passwordSchema = useMemo(
    () =>
      Yup.object({
        current_password: Yup.string().required(
          t("admin.dashboard.profile.password.validation.currentRequired")
        ),
        password: Yup.string()
          .min(8, t("admin.dashboard.profile.password.validation.min"))
          .required(t("admin.dashboard.profile.password.validation.required")),
        password_confirmation: Yup.string()
          .oneOf(
            [Yup.ref("password")],
            t("admin.dashboard.profile.password.validation.confirmed")
          )
          .required(
            t("admin.dashboard.profile.password.validation.confirmedRequired")
          ),
      }),
    [t]
  );

  const passwordForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      current_password: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      setSubmitting(true);

      try {
        const response = await superAdminUpdatePassword(values);
        toast.success(
          response.message || t("admin.dashboard.profile.password.toast.success")
        );
        closePasswordModal();
      } catch (error: unknown) {
        toast.error(
          (error as { data?: string })?.data ||
            t("admin.dashboard.profile.password.toast.error")
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const toggleProfileDropdown = () => {
    setIsProfileDropdown((open) => !open);
  };

  const openPasswordModal = () => {
    setIsProfileDropdown(false);
    passwordForm.resetForm();
    setPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    passwordForm.resetForm();
  };

  const handleLogout = async () => {
    await superAdminLogout();
    dispatch(logoutUserSuccess(true));
    navigate("/super-admin/login");
  };

  return (
    <>
      <Dropdown
        isOpen={isProfileDropdown}
        toggle={toggleProfileDropdown}
        className="ms-sm-3 header-item topbar-user"
      >
        <DropdownToggle tag="button" type="button" className="btn">
          <span className="d-flex align-items-center">
            <img
              className="rounded-circle header-profile-user"
              src={avatar1}
              alt={t("admin.dashboard.profile.avatarAlt")}
            />
            <span className="text-start ms-xl-2">
              <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
                {userName}
              </span>
              <span className="d-none d-xl-block ms-1 fs-12 text-muted user-name-sub-text">
                {t("admin.dashboard.profile.role")}
              </span>
            </span>
          </span>
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-end">
          <h6 className="dropdown-header">
            {t("admin.dashboard.profile.welcome", { name: userName })}
          </h6>
          <DropdownItem className="p-0" onClick={openPasswordModal}>
            <span className="dropdown-item">
              <i className="mdi mdi-lock-reset text-muted fs-16 align-middle me-1" />
              <span className="align-middle">
                {t("admin.dashboard.profile.changePassword")}
              </span>
            </span>
          </DropdownItem>
          <DropdownItem
            className="p-0"
            onClick={() => {
              const next = !beepMuted;
              setSupportBeepMuted(next);
              setBeepMuted(next);
            }}
          >
            <span className="dropdown-item">
              <i
                className={`mdi ${
                  beepMuted ? "mdi-volume-high" : "mdi-volume-off"
                } text-muted fs-16 align-middle me-1`}
              />
              <span className="align-middle">
                {beepMuted
                  ? t("admin.dashboard.profile.unmuteSupportBeep")
                  : t("admin.dashboard.profile.muteSupportBeep")}
              </span>
            </span>
          </DropdownItem>
          <div className="dropdown-item-text px-3 pb-2">
            <Label
              htmlFor="sa-support-poll-interval"
              className="form-label small text-muted mb-1"
            >
              {t("admin.dashboard.profile.pollInterval")}
            </Label>
            <Input
              id="sa-support-poll-interval"
              type="select"
              bsSize="sm"
              value={pollBaseMs === null ? "auto" : String(pollBaseMs)}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                const value = event.target.value;
                if (value === "auto") {
                  setSupportPollBaseMs(null);
                  setPollBaseMsState(null);
                  return;
                }

                const next = Number(value) as SupportPollBaseMs;
                if (SUPPORT_POLL_BASE_OPTIONS_MS.includes(next)) {
                  setSupportPollBaseMs(next);
                  setPollBaseMsState(next);
                }
              }}
            >
              <option value="auto">
                {t("admin.dashboard.profile.pollIntervalAuto")}
              </option>
              {SUPPORT_POLL_BASE_OPTIONS_MS.map((ms) => (
                <option key={ms} value={ms}>
                  {t("admin.dashboard.profile.pollIntervalSeconds", {
                    seconds: ms / 1000,
                  })}
                </option>
              ))}
            </Input>
            <small className="text-muted d-block mt-1">
              {t("admin.dashboard.profile.pollIntervalHint")}
            </small>
          </div>
          <DropdownItem className="p-0" onClick={handleLogout}>
            <span className="dropdown-item">
              <i className="mdi mdi-logout text-muted fs-16 align-middle me-1" />
              <span className="align-middle">
                {t("admin.dashboard.profile.logout")}
              </span>
            </span>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <Modal isOpen={passwordModalOpen} toggle={closePasswordModal} centered>
        <ModalHeader toggle={closePasswordModal}>
          {t("admin.dashboard.profile.password.title")}
        </ModalHeader>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            passwordForm.handleSubmit();
          }}
        >
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="sa-current-password">
                {t("admin.dashboard.profile.password.current")}
              </Label>
              <Input
                id="sa-current-password"
                name="current_password"
                type="password"
                value={passwordForm.values.current_password}
                onChange={passwordForm.handleChange}
                onBlur={passwordForm.handleBlur}
                invalid={
                  !!(
                    passwordForm.touched.current_password &&
                    passwordForm.errors.current_password
                  )
                }
              />
              {passwordForm.touched.current_password &&
              passwordForm.errors.current_password ? (
                <FormFeedback>
                  {passwordForm.errors.current_password}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label htmlFor="sa-new-password">
                {t("admin.dashboard.profile.password.new")}
              </Label>
              <Input
                id="sa-new-password"
                name="password"
                type="password"
                value={passwordForm.values.password}
                onChange={passwordForm.handleChange}
                onBlur={passwordForm.handleBlur}
                invalid={
                  !!(
                    passwordForm.touched.password && passwordForm.errors.password
                  )
                }
              />
              {passwordForm.touched.password && passwordForm.errors.password ? (
                <FormFeedback>{passwordForm.errors.password}</FormFeedback>
              ) : null}
            </div>
            <div className="mb-0">
              <Label htmlFor="sa-confirm-password">
                {t("admin.dashboard.profile.password.confirm")}
              </Label>
              <Input
                id="sa-confirm-password"
                name="password_confirmation"
                type="password"
                value={passwordForm.values.password_confirmation}
                onChange={passwordForm.handleChange}
                onBlur={passwordForm.handleBlur}
                invalid={
                  !!(
                    passwordForm.touched.password_confirmation &&
                    passwordForm.errors.password_confirmation
                  )
                }
              />
              {passwordForm.touched.password_confirmation &&
              passwordForm.errors.password_confirmation ? (
                <FormFeedback>
                  {passwordForm.errors.password_confirmation}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              type="button"
              onClick={closePasswordModal}
              disabled={submitting}
            >
              {t("admin.dashboard.profile.password.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("admin.dashboard.profile.password.saving")}
                </>
              ) : (
                t("admin.dashboard.profile.password.save")
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default SuperAdminProfileDropdown;
